import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Typography } from '@mui/material';
import AssessmentReview from '../components/admin/AssessmentReview';
import theme from '../theme';
import i18n from '../i18n';
import { recordingAPI } from './api';
import { sanitizeFilename } from './pdfExport';

const REPORT_WIDTH = 1120;
const MEDIA_TIMEOUT = 15000;

// A separate video leaves the reviewer's playback position and controls untouched.
export function captureVideoFrame(url, signal) {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    let timer;
    const finish = (image = null) => {
      clearTimeout(timer);
      video.removeEventListener('loadeddata', capture);
      video.removeEventListener('error', failed);
      signal?.removeEventListener('abort', failed);
      video.removeAttribute('src');
      video.load();
      resolve(image);
    };
    const failed = () => finish();
    const capture = () => {
      try {
        if (!video.videoWidth || !video.videoHeight) {
          finish();
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(video.videoWidth, REPORT_WIDTH);
        canvas.height = Math.round(canvas.width * video.videoHeight / video.videoWidth);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/png'));
      } catch {
        // Unsupported media or missing CORS permission must not block the report.
        finish();
      }
    };
    if (signal?.aborted) {
      resolve(null);
      return;
    }
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.muted = true;
    video.addEventListener('loadeddata', capture);
    video.addEventListener('error', failed);
    signal?.addEventListener('abort', failed, { once: true });
    timer = setTimeout(failed, MEDIA_TIMEOUT);
    video.src = url;
    video.load();
  });
}

async function waitForAssets(doc, report, signal) {
  await Promise.all([
    doc.fonts?.ready,
    ...Array.from(report.querySelectorAll('img'), (img) => new Promise((resolve) => {
      if (img.complete || signal.aborted) {
        resolve();
        return;
      }
      const done = () => {
        clearTimeout(timer);
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        signal.removeEventListener('abort', done);
        resolve();
      };
      const timer = setTimeout(done, MEDIA_TIMEOUT);
      img.addEventListener('load', done);
      img.addEventListener('error', done);
      signal.addEventListener('abort', done, { once: true });
    })),
  ]);
}

export async function openAssessmentExport({ candidate, assignment, limitedAccess = false }) {
  // Open before any await so this remains a direct response to the user's click.
  const exportWindow = window.open('', '_blank');
  if (!exportWindow) {
    throw new Error(i18n.t('admin.grading.exportPopupBlocked', 'Allow pop-ups for this site to open the assessment export.'));
  }
  const doc = exportWindow.document;
  doc.documentElement.lang = i18n.resolvedLanguage || 'en';
  doc.title = `assessment_${sanitizeFilename(candidate.email)}_${sanitizeFilename(assignment.assessment_name)}_${new Date().toISOString().split('T')[0]}.pdf`;

  const styles = doc.createElement('style');
  styles.textContent = `
    html, body { margin: 0; padding: 0; background: white; }
    .assessment-export { width: ${REPORT_WIDTH}px; padding: 24px; box-sizing: border-box; overflow-wrap: anywhere; background: white; }
    .assessment-export * { min-width: 0; }
    .assessment-export pre, .assessment-export code { white-space: pre-wrap; overflow: visible; overflow-wrap: anywhere; }
    .assessment-export table { width: 100%; table-layout: fixed; }
    .assessment-export img { max-width: 100%; }
    .export-toolbar { padding: 16px 24px; font: 14px Arial, sans-serif; }
    .export-toolbar button { padding: 8px 16px; cursor: pointer; }
    @media print {
      html, body { width: ${REPORT_WIDTH}px; margin: 0 !important; padding: 0 !important; }
      .export-toolbar { display: none !important; }
      .assessment-export { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
    }
  `;
  const pageStyle = doc.createElement('style');
  doc.head.append(styles, pageStyle);
  const toolbar = doc.createElement('div');
  toolbar.className = 'export-toolbar';
  const printButton = doc.createElement('button');
  printButton.textContent = i18n.t('admin.grading.exportPrint', 'Print / Save as PDF');
  printButton.disabled = true;
  const status = doc.createElement('p');
  status.textContent = i18n.t('admin.grading.exportPreparing', 'Preparing assessment and video previews…');
  toolbar.append(printButton, status);
  const report = doc.createElement('main');
  report.className = 'assessment-export';
  doc.body.append(toolbar, report);

  const controller = new AbortController();
  const cache = createCache({ key: 'assessment-export', container: doc.head, prepend: true });
  const root = createRoot(report);
  const measurePage = () => {
    // Leave bottom allowance for small differences between screen and print layout.
    const height = Math.ceil(Math.max(report.scrollHeight, report.getBoundingClientRect().height)) + 64;
    pageStyle.textContent = `@page { size: ${REPORT_WIDTH}px ${height}px; margin: 0; }`;
  };
  const cleanup = () => {
    controller.abort();
    exportWindow.removeEventListener('beforeprint', measurePage);
    root.unmount();
    cache.sheet.flush();
  };
  exportWindow.addEventListener('pagehide', cleanup, { once: true });

  try {
    const frames = await Promise.all(assignment.questions.map(async (question) => [
      question.recording.id,
      await captureVideoFrame(recordingAPI.getScreenRecording(question.recording.id), controller.signal),
    ]));
    if (controller.signal.aborted || exportWindow.closed) return;

    flushSync(() => root.render(
      <CacheProvider value={cache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Typography variant="h6" gutterBottom>Grading: {candidate.email}</Typography>
          <AssessmentReview assignment={assignment} limitedAccess={limitedAccess}
            exportMode snapshots={Object.fromEntries(frames)} />
        </ThemeProvider>
      </CacheProvider>
    ));
    await waitForAssets(doc, report, controller.signal);
    if (controller.signal.aborted || exportWindow.closed) return;

    measurePage();
    exportWindow.addEventListener('beforeprint', measurePage);
    printButton.onclick = () => {
      measurePage();
      exportWindow.focus();
      exportWindow.print();
    };
    status.textContent = i18n.t('admin.grading.exportPrintHelp', 'In Chrome, choose Save as PDF, use 100% scale, and turn off headers and footers.');
    printButton.disabled = false;
  } catch (error) {
    status.textContent = i18n.t('admin.grading.exportFailed', 'Could not prepare the assessment. Close this tab and try exporting again.');
    throw error;
  }
}
